import { createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import { GlobalSearchResponse } from 'Types/globalSearch';

import { StatusError } from '../../components/Providers/ErrorProvider';
import { API_URL, enrichedFetch } from '../../services/api';
import {
  Conversation,
  ConversationResponse,
  CreateConversation,
  CreateMessage,
  CreateMessagePrivate,
  DownloadConversationResponse,
  InternalCreateLongRunningMessage,
  Message,
  MessageFeedback,
  UpdateConversation,
} from '../../Types/conversation';
import { AppEnum } from '../../Types/enums';
import { LlmEnum, llms } from '../../Types/llm';
import { PaginatedResult } from '../../Types/pagination';
import { User } from '../../Types/user';
import { conversationsPathUrl } from '../../util/routesUrls';
import { CONVERSATION_LIST_SIZE, GLOBAL_RESULTS_LIST_SIZE } from '../consts';
import {
  setFolderAgnosticPage,
  setIsLoadingConversations,
  setPage,
} from '../reducers/conversationReducer';
import { AppDispatch, RootState } from '../store';

export const getConversations = createAsyncThunk<
  PaginatedResult<Conversation>,
  { pageAction?: 'increment' | 'reset'; pinned?: boolean; searchQuery?: string } | void,
  { state: RootState }
>('conversations/fetchConversations', async (arg, { getState, dispatch, rejectWithValue }) => {
  const { page, searchQuery: stateSearchQuery } = getState().conversations;
  const { selectedApp } = getState().appStatus;
  const effectiveQuery = (arg?.searchQuery ?? stateSearchQuery).trim();

  const pageAction = arg?.pageAction;

  let newPage = page;
  if (pageAction === 'increment') {
    newPage += 1;
  } else if (pageAction === 'reset') {
    newPage = 0;
  }
  dispatch(setIsLoadingConversations(true));
  dispatch(setPage(newPage));

  try {
    const url = `${API_URL}/${conversationsPathUrl(
      selectedApp,
      effectiveQuery,
      arg?.pinned,
      undefined,
      newPage,
      CONVERSATION_LIST_SIZE
    )}`;

    const response = (await enrichedFetch(url)) as PaginatedResult<Conversation>;
    return response;
  } catch {
    enqueueSnackbar('Failed to fetch conversations', { variant: 'error' });
    return rejectWithValue('Failed to fetch conversations');
  }
});

let currentFolderAgnosticConversationsController: AbortController | null = null;
export const getFolderAgnosticConversations = createAsyncThunk<
  ConversationResponse,
  { pageAction?: 'increment' | 'reset'; pinned?: boolean; foldersWithProjects?: boolean } | void,
  { state: RootState }
>(
  'conversations/fetchFolderAgnosticConversations',
  async (arg, { getState, dispatch, rejectWithValue }) => {
    // Cancel any ongoing request
    if (currentFolderAgnosticConversationsController) {
      currentFolderAgnosticConversationsController.abort(
        'Duplicate request: Aborting previous folder-agnostic conversation fetch'
      );
    }

    // Create a new controller for this request
    currentFolderAgnosticConversationsController = new AbortController();
    const signal = currentFolderAgnosticConversationsController.signal;

    const { folderAgnosticPage } = getState().conversations;
    const { selectedApp } = getState().appStatus;

    const pageAction = arg?.pageAction;
    const foldersWithProjects = arg?.foldersWithProjects ?? selectedApp === AppEnum.DEAL_GPT;

    let newPage = folderAgnosticPage;
    if (pageAction === 'increment') {
      newPage += 1;
    } else if (pageAction === 'reset') {
      newPage = 1;
    }
    const pageOffset = GLOBAL_RESULTS_LIST_SIZE * (newPage - 1);

    dispatch(setFolderAgnosticPage(newPage));

    try {
      const url = `${API_URL}/globalSearch?offset=${pageOffset}&conversationsOnly=${true}&foldersWithProjects=${foldersWithProjects}`;

      const response = (await enrichedFetch(url, { signal })) as GlobalSearchResponse;
      currentFolderAgnosticConversationsController = null;
      return {
        data: response.conversations,
        totalCount: response.totalCount,
      } as ConversationResponse;
    } catch (error) {
      // Don't show error notification if request was deliberately canceled. We have two abort messages (Fetch aborted, The user aborted a request.)
      if (
        (error as Error)?.message === 'Fetch aborted' ||
        (error as Error)?.message === 'The user aborted a request.'
      ) {
        return { data: [], totalCount: 0 };
      }

      enqueueSnackbar('Failed to fetch conversations', { variant: 'error' });
      return rejectWithValue('Failed to fetch conversations');
    }
  }
);

export const getConversation = createAsyncThunk<Conversation, Conversation['id']>(
  'conversations/getConversation',
  async (id, { rejectWithValue }) => {
    try {
      const response = (await enrichedFetch(`${API_URL}/conversations/${id}`)) as Conversation;
      return response;
    } catch {
      enqueueSnackbar('Failed to fetch conversation', { variant: 'error' });
      return rejectWithValue('Failed to fetch conversation');
    }
  }
);

export const getSharedConversation = createAsyncThunk<Conversation, Conversation['id']>(
  'conversations/getSharedConversation',
  async (id, { rejectWithValue }) => {
    try {
      const response = (await enrichedFetch(
        `${API_URL}/conversations/shared/${id}`
      )) as Conversation;
      return response;
    } catch {
      enqueueSnackbar('Failed to fetch conversation', { variant: 'error' });
      return rejectWithValue('Failed to fetch conversation');
    }
  }
);

export const getStreamingResponse = createAsyncThunk<
  Response | unknown,
  {
    message: CreateMessage;
    userId: User['id'];
    llm: LlmEnum;
    indexId?: string;
    signal?: AbortSignal;
    selectedApp?: AppEnum;
  }
>(
  'conversations/getStreamingResponse',
  async ({ message, userId, llm, selectedApp, indexId }, { rejectWithValue }) => {
    try {
      const result = await enrichedFetch(
        `${API_URL}/streaming`,
        {
          method: 'POST',
          body: JSON.stringify({
            message,
            userId,
            llm: llms[llm].deploymentId,
            selectedApp,
            indexId,
          }),
          headers: {
            'Content-Type': 'application/json',
          },
        },
        0,
        false
      );
      return result;
    } catch {
      return rejectWithValue('Failed to fetch streaming response');
    }
  }
);

export const getStreamingResponseTemporary = createAsyncThunk<
  Response | unknown,
  {
    message: CreateMessagePrivate;
    messageArray: Message[];
    userId: User['id'];
    llm: LlmEnum;
    signal?: AbortSignal;
  }
>(
  'conversations/getStreamingResponseTemporary',
  async ({ message, messageArray, userId, llm, signal }, { rejectWithValue }) => {
    try {
      const result = await enrichedFetch(
        `${API_URL}/streaming/temporary`,
        {
          method: 'POST',
          body: JSON.stringify({ message, messageArray, userId, llm: llms[llm].deploymentId }),
          headers: {
            'Content-Type': 'application/json',
          },
        },
        0,
        false,
        signal
      );
      return result;
    } catch {
      return rejectWithValue('Failed to fetch streaming response');
    }
  }
);

export const postConversation = createAsyncThunk<
  Conversation,
  CreateConversation,
  { dispatch: AppDispatch }
>('conversations/postConversation', async (conversation, { rejectWithValue }) => {
  try {
    const response = (await enrichedFetch(`${API_URL}/conversations`, {
      method: 'POST',
      body: JSON.stringify(conversation),
      headers: {
        'Content-Type': 'application/json',
      },
    })) as Conversation;
    return response;
  } catch {
    enqueueSnackbar('Failed to post conversation', { variant: 'error' });
    return rejectWithValue('Failed to post conversation');
  }
});

export const postConvoFromMessage = createAsyncThunk<
  Conversation,
  Message,
  { dispatch: AppDispatch }
>('conversations/postConvoFromMessage', async (message, { dispatch, rejectWithValue }) => {
  try {
    const response = (await enrichedFetch(`${API_URL}/conversations/branch`, {
      method: 'POST',
      body: JSON.stringify(message),
      headers: {
        'Content-Type': 'application/json',
      },
    })) as Conversation;

    dispatch(getConversations({ pageAction: 'reset' }));

    return response;
  } catch {
    enqueueSnackbar('Failed to post conversation from message', { variant: 'error' });
    return rejectWithValue('Failed to post conversation from message');
  }
});

export const regenerateMessage = createAsyncThunk<
  Conversation,
  { message: Message; llm: LlmEnum },
  { dispatch: AppDispatch }
>('conversations/regenerateMessage', async ({ message, llm }, { dispatch, rejectWithValue }) => {
  try {
    const response = (await enrichedFetch(`${API_URL}/conversations/regenerate`, {
      method: 'POST',
      body: JSON.stringify({ message, llm: llms[llm].deploymentId }),
      headers: {
        'Content-Type': 'application/json',
      },
    })) as Conversation;

    dispatch(getConversations({ pageAction: 'reset' }));

    return response;
  } catch (error: unknown) {
    if ((error as { status?: number })?.status === 403) {
      return rejectWithValue({ status: 403 });
    }
    enqueueSnackbar('Failed to regenerate message', { variant: 'error' });
    return rejectWithValue('Failed to regenerate message');
  }
});

export const updateConversation = createAsyncThunk<
  void,
  { updatedFields: UpdateConversation; conversationId: Conversation['id'] },
  { dispatch: AppDispatch }
>(
  'conversations/updateConversation',
  async ({ updatedFields, conversationId }, { rejectWithValue }) => {
    try {
      await enrichedFetch(`${API_URL}/conversations/${conversationId}`, {
        method: 'PUT',
        body: JSON.stringify(updatedFields),
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch {
      enqueueSnackbar('Failed to update conversation', { variant: 'error' });
      return rejectWithValue('Failed to update conversation');
    }
  }
);

export const deleteConversation = createAsyncThunk<
  void,
  Conversation['id'],
  { dispatch: AppDispatch }
>('conversations/deleteConversation', async (conversationId, { rejectWithValue }) => {
  try {
    await enrichedFetch(`${API_URL}/conversations/${conversationId}`, {
      method: 'DELETE',
    });
  } catch {
    enqueueSnackbar('Failed to delete conversation', { variant: 'error' });
    return rejectWithValue('Failed to delete conversation');
  }
});

export const getMessages = createAsyncThunk<Message[], Conversation['id']>(
  'conversations/getMessages',
  async (conversationId, { rejectWithValue }) => {
    try {
      const response = (await enrichedFetch(
        `${API_URL}/conversations/messages/${conversationId}`
      )) as Message[];
      return response;
    } catch {
      enqueueSnackbar('Failed to fetch messages', { variant: 'error' });
      return rejectWithValue('Failed to fetch messages');
    }
  }
);

export const postMessage = createAsyncThunk<
  Message,
  { message: CreateMessage; llm: LlmEnum; indexId?: string },
  { dispatch: AppDispatch }
>('conversations/postMessage', async ({ message, llm, indexId }, { rejectWithValue }) => {
  try {
    const response = (await enrichedFetch(`${API_URL}/conversations/messages`, {
      method: 'POST',
      body: JSON.stringify({ message, llm: llms[llm].deploymentId, indexId }),
      headers: {
        'Content-Type': 'application/json',
      },
    })) as Message;
    return response;
  } catch {
    enqueueSnackbar('Failed to post message', { variant: 'error' });
    return rejectWithValue('Failed to post message');
  }
});

export const patchMessage = createAsyncThunk<
  Message,
  { messageId: Message['id']; updatedFields: Partial<Message> },
  { dispatch: AppDispatch }
>('conversations/patchMessage', async ({ messageId, updatedFields }, { rejectWithValue }) => {
  try {
    const response = (await enrichedFetch(`${API_URL}/conversations/messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify({ message: updatedFields }),
      headers: {
        'Content-Type': 'application/json',
      },
    })) as Message;
    return response;
  } catch {
    enqueueSnackbar('Failed to update message', { variant: 'error' });
    return rejectWithValue('Failed to update message');
  }
});

export const postLongRunningMessage = createAsyncThunk<
  void,
  { message: InternalCreateLongRunningMessage },
  { dispatch: AppDispatch }
>('conversations/postMessageLongRunning', async ({ message }, { rejectWithValue }) => {
  try {
    const response = (await enrichedFetch(`${API_URL}/conversations/messages/long-running`, {
      method: 'POST',
      body: JSON.stringify({ message }),
      headers: {
        'Content-Type': 'application/json',
      },
    })) as string;

    if (response === 'No long running tool found') {
      enqueueSnackbar('No long running tool found', { variant: 'warning' });
    } else {
      enqueueSnackbar(response, { variant: 'info' });
    }
  } catch (error) {
    if (
      error instanceof StatusError &&
      error.status === 403 &&
      error.message.startsWith('Insufficient research credits')
    ) {
      enqueueSnackbar(error.message, { variant: 'warning' });
    } else if (error instanceof StatusError && error.status === 501) {
      enqueueSnackbar('Deep research is not implemented for this LLM', { variant: 'error' });
      return rejectWithValue('Deep research is not implemented for this LLM');
    } else {
      enqueueSnackbar('Failed to post message', { variant: 'error' });
      return rejectWithValue('Failed to post message');
    }
  }
});

export const downloadFile = createAsyncThunk<DownloadConversationResponse, Conversation['id']>(
  'conversations/downloadFile',
  async (conversationId, { rejectWithValue }) => {
    try {
      const response = (await enrichedFetch(`${API_URL}/conversations/download/${conversationId}`, {
        method: 'GET',
      })) as DownloadConversationResponse;
      return response;
    } catch {
      enqueueSnackbar('Failed to download file', { variant: 'error' });
      return rejectWithValue('Failed to download file');
    }
  }
);

export const updateFeedback = createAsyncThunk<
  void,
  { messageId: Message['id']; messageFeedback: MessageFeedback }
>('conversations/updateFeedback', async ({ messageId, messageFeedback }, { rejectWithValue }) => {
  try {
    await enrichedFetch(`${API_URL}/conversations/messages/messageFeedback/${messageId}`, {
      method: 'PUT',
      body: JSON.stringify({ messageFeedback }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  } catch {
    enqueueSnackbar('Failed to update feedback', { variant: 'error' });
    return rejectWithValue('Failed to update feedback');
  }
});
